/* ==========================================================================
   Leaf Landing Page — shared behavior
   --------------------------------------------------------------------------
   One small controller registry for the reusable `leaf-lp-*` sections.
   Each section renders a root with `data-leaf-lp="<type>"`; on load (and on
   Shopify section:load in the editor) we initialize the matching controller.
   Controllers are added here as their sections are built:
     - carousel  (proof strip, reviews)
     - buybox    (gallery / variant / gifts / ATC)
     - giftmodal (gift tag popup)
     - sticky    (mobile sticky ATC state machine)
   Cross-section signal: the buy box broadcasts `leaf-lp:buybox` (CustomEvent)
   with { variantId, colorName, priceCents, compareCents, available } so the
   sticky ATC can mirror it — same pattern as fd26:state.
   ========================================================================== */
(function () {
  if (window.LeafLP) return;

  var LeafLP = (window.LeafLP = {});
  var registry = {};

  LeafLP.register = function (type, initFn) { registry[type] = initFn; };

  // Consistent USD formatting that matches the design ("$109", "$166.90"):
  // whole amounts show no cents, fractional amounts show two. Deliberately does
  // NOT defer to Shopify.formatMoney, whose money_format would force "$109.00".
  LeafLP.money = function (cents) {
    if (cents == null || isNaN(cents)) return '';
    var n = cents / 100;
    return '$' + (n % 1 === 0 ? n.toFixed(0) : n.toFixed(2));
  };

  // Parse a display price string ("$109", "$1,099.00") back to cents, for the
  // promo-display path where the shown price is a CMS string, not variant data.
  LeafLP.toCents = function (str) {
    if (str == null) return null;
    var n = parseFloat(String(str).replace(/[^0-9.]/g, ''));
    return isNaN(n) ? null : Math.round(n * 100);
  };

  LeafLP.onReady = function (fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  };

  // Initialize every controller root inside `scope` (default: document) that
  // hasn't been initialized yet. A controller may return a cleanup function,
  // run on shopify:section:unload so editor edits don't leak timers/listeners.
  LeafLP.init = function (scope) {
    scope = scope || document;
    var roots = scope.querySelectorAll('[data-leaf-lp]');
    roots.forEach(function (root) {
      if (root.__leafLpInit) return;
      var type = root.getAttribute('data-leaf-lp');
      var fn = registry[type];
      if (!fn) return;
      root.__leafLpInit = true;
      try { root.__leafLpCleanup = fn(root) || null; }
      catch (e) { if (window.console) console.error('[leaf-lp]', type, e); }
    });
  };

  function teardown(scope) {
    (scope || document).querySelectorAll('[data-leaf-lp]').forEach(function (root) {
      if (typeof root.__leafLpCleanup === 'function') { try { root.__leafLpCleanup(); } catch (e) {} }
      root.__leafLpCleanup = null;
      root.__leafLpInit = false;
    });
  }

  // Smooth-scroll for ANY in-page anchor link (CTAs → buy box, etc.). This file
  // only loads on pages that use leaf-lp-* sections (the landing pages), so the
  // handler stays scoped to the LP — the homepage hero, which reuses hero-new
  // without loading this asset, keeps its native jump behavior untouched.
  // (Broadened from `.leaf-lp-scroll`-only so hero-new's plain anchor CTA and
  // any future CTA smooth-scroll uniformly.)
  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('a[href^="#"]');
    if (!a) return;
    var id = a.getAttribute('href');
    if (!id || id === '#') return;
    var target = document.querySelector(id);
    if (!target) return;
    e.preventDefault();
    var y = target.getBoundingClientRect().top + (window.pageYOffset || document.documentElement.scrollTop) - 12;
    window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
  });

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Proof strip — desktop shows all items inline (CSS); mobile shows one at a
  // time with prev/next arrows, swipe, and optional autoplay that resets on
  // any manual interaction. Autoplay only advances while the mobile layout is
  // active (arrows visible).
  LeafLP.register('proofstrip', function (root) {
    // Scroll (marquee) mode is a pure-CSS animation — no carousel controller.
    if (root.getAttribute('data-mobile-behavior') === 'scroll') return;
    var items = Array.prototype.slice.call(root.querySelectorAll('[data-lp-proof-item]'));
    if (!items.length) return;
    var prev = root.querySelector('[data-lp-proof-prev]');
    var next = root.querySelector('[data-lp-proof-next]');
    var autoplay = root.getAttribute('data-mobile-behavior') === 'autoplay';
    var interval = parseInt(root.getAttribute('data-interval'), 10) || 2500;
    var i = 0, timer = null, paused = false;

    function show(n) {
      i = (n + items.length) % items.length;
      items.forEach(function (el, k) { el.classList.toggle('is-active', k === i); });
    }
    function mobileActive() { return prev && getComputedStyle(prev).display !== 'none'; }
    function start() {
      stop();
      if (!autoplay || reduceMotion || paused) return;
      timer = setInterval(function () { if (mobileActive()) show(i + 1); }, interval);
    }
    function stop() { if (timer) { clearInterval(timer); timer = null; } }
    function step(d) { show(i + d); start(); } // reset timer on manual interaction

    function onPrev() { step(-1); }
    function onNext() { step(1); }
    if (prev) prev.addEventListener('click', onPrev);
    if (next) next.addEventListener('click', onNext);

    var x0 = null, y0 = null;
    function onTouchStart(e) { x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; }
    function onTouchEnd(e) {
      if (x0 == null) return;
      var dx = e.changedTouches[0].clientX - x0;
      var dy = e.changedTouches[0].clientY - y0;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) step(dx < 0 ? 1 : -1);
      x0 = y0 = null;
    }
    root.addEventListener('touchstart', onTouchStart, { passive: true });
    root.addEventListener('touchend', onTouchEnd, { passive: true });

    // WCAG 2.2.2 — pause auto-advance on hover/focus.
    function pause() { paused = true; stop(); }
    function resume() { paused = false; start(); }
    root.addEventListener('mouseenter', pause);
    root.addEventListener('mouseleave', resume);
    root.addEventListener('focusin', pause);
    root.addEventListener('focusout', resume);

    function onVis() { document.hidden ? stop() : start(); }
    document.addEventListener('visibilitychange', onVis);

    show(0);
    start();

    return function cleanup() {
      stop();
      if (prev) prev.removeEventListener('click', onPrev);
      if (next) next.removeEventListener('click', onNext);
      root.removeEventListener('touchstart', onTouchStart);
      root.removeEventListener('touchend', onTouchEnd);
      root.removeEventListener('mouseenter', pause);
      root.removeEventListener('mouseleave', resume);
      root.removeEventListener('focusin', pause);
      root.removeEventListener('focusout', resume);
      document.removeEventListener('visibilitychange', onVis);
    };
  });

  // ── Reviews carousel ─────────────────────────────────────────────────────
  // Desktop: shows N cards (measured), auto-rotates every interval, chevrons +
  // hover/focus pause. Mobile: 'swipe' (1-up + swipe) or 'stacked' (all shown,
  // no carousel) via a CSS class. Reduced-motion disables autoplay.
  LeafLP.register('reviews', function (root) {
    var viewport = root.querySelector('[data-lp-rev-viewport]');
    var track = root.querySelector('[data-lp-rev-track]');
    if (!viewport || !track) return;
    var cards = Array.prototype.slice.call(track.children);
    if (cards.length < 2) return;
    var prev = root.querySelector('[data-lp-rev-prev]');
    var next = root.querySelector('[data-lp-rev-next]');
    var autoplay = root.getAttribute('data-autoplay') === 'true';
    var interval = parseInt(root.getAttribute('data-interval'), 10) || 5000;
    var i = 0, timer = null, paused = false;

    function gap() {
      var cs = getComputedStyle(track);
      return parseFloat(cs.columnGap || cs.gap || 0) || 0;
    }
    function metrics() {
      var stacked = getComputedStyle(track).flexDirection === 'column';
      var step = cards[0].getBoundingClientRect().width + gap();
      var visible = step > 0 ? Math.max(1, Math.round(viewport.clientWidth / step)) : 1;
      return { stacked: stacked, step: step, visible: visible, maxI: Math.max(0, cards.length - visible) };
    }
    function apply() {
      var m = metrics();
      if (m.stacked) { track.style.transform = ''; return; }
      if (i > m.maxI) i = 0;
      track.style.transform = 'translateX(' + (-i * m.step) + 'px)';
    }
    function go(d) { var m = metrics(); i += d; if (i < 0) i = m.maxI; if (i > m.maxI) i = 0; apply(); start(); }
    function start() {
      stop();
      if (!autoplay || reduceMotion || paused) return;
      // Desktop only: don't auto-rotate the 1-up mobile view (WCAG 2.2.2 — no pause on touch).
      var m = metrics(); if (m.stacked || m.maxI === 0 || m.visible <= 1) return;
      timer = setInterval(function () { var mm = metrics(); i = (i + 1 > mm.maxI) ? 0 : i + 1; apply(); }, interval);
    }
    function stop() { if (timer) { clearInterval(timer); timer = null; } }

    function onPrev() { go(-1); }
    function onNext() { go(1); }
    if (prev) prev.addEventListener('click', onPrev);
    if (next) next.addEventListener('click', onNext);

    var x0 = null, y0 = null;
    function ts(e) { x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; }
    function te(e) { if (x0 == null) return; var dx = e.changedTouches[0].clientX - x0, dy = e.changedTouches[0].clientY - y0; if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) go(dx < 0 ? 1 : -1); x0 = y0 = null; }
    viewport.addEventListener('touchstart', ts, { passive: true });
    viewport.addEventListener('touchend', te, { passive: true });

    function pause() { paused = true; stop(); }
    function resume() { paused = false; start(); }
    root.addEventListener('mouseenter', pause);
    root.addEventListener('mouseleave', resume);
    root.addEventListener('focusin', pause);
    root.addEventListener('focusout', resume);
    function onVis() { document.hidden ? stop() : start(); }
    document.addEventListener('visibilitychange', onVis);
    var onResize = function () { apply(); start(); };
    window.addEventListener('resize', onResize);

    apply(); start();
    return function cleanup() {
      stop();
      if (prev) prev.removeEventListener('click', onPrev);
      if (next) next.removeEventListener('click', onNext);
      viewport.removeEventListener('touchstart', ts);
      viewport.removeEventListener('touchend', te);
      root.removeEventListener('mouseenter', pause);
      root.removeEventListener('mouseleave', resume);
      root.removeEventListener('focusin', pause);
      root.removeEventListener('focusout', resume);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('resize', onResize);
    };
  });

  // ── Buy box ────────────────────────────────────────────────────────────
  // Reads a JSON data island, drives gallery (main + thumbs + arrows + swipe),
  // color swatches (→ swaps gallery slots 1–2, color label, ATC price), and
  // real add-to-cart (/cart/add.js + hidden tracking props + drawer refresh).
  // Broadcasts `leaf-lp:buybox` so the sticky ATC can mirror price/variant.
  LeafLP.register('buybox', function (root) {
    var dataEl = root.querySelector('[data-lp-buybox-data]');
    var data;
    try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }
    if (!data || !data.colors || !data.colors.length) return;

    var ci = 0, ii = 0;
    var mainImg   = root.querySelector('[data-lp-main-img]');
    var thumbsEl  = root.querySelector('[data-lp-thumbs]');
    var colorName = root.querySelector('[data-lp-color-name]');
    var swatches  = Array.prototype.slice.call(root.querySelectorAll('[data-lp-swatch]'));
    var atcBtn    = root.querySelector('[data-lp-atc]');
    var priceNow  = root.querySelector('[data-lp-price-now]');
    var priceWas  = root.querySelector('[data-lp-price-was]');
    var giftPrice = root.querySelector('[data-lp-gift-total]');
    var headerImg = root.querySelector('[data-lp-header-img]');

    function gallery() { return (data.colors[ci].images || []).concat(data.sharedImages || []).filter(Boolean); }

    function paintMain() {
      var g = gallery();
      ii = Math.min(ii, g.length - 1); if (ii < 0) ii = 0;
      if (mainImg && g[ii]) { mainImg.src = g[ii]; mainImg.alt = data.colors[ci].name + ' — Leaf Two'; }
      if (thumbsEl) {
        Array.prototype.forEach.call(thumbsEl.children, function (t, k) {
          t.classList.toggle('is-active', k === ii);
          t.setAttribute('aria-current', k === ii ? 'true' : 'false');
        });
      }
    }
    function buildThumbs() {
      if (!thumbsEl) return;
      var g = gallery();
      thumbsEl.innerHTML = '';
      g.forEach(function (src, k) {
        var b = document.createElement('button');
        b.type = 'button'; b.className = 'leaf-lp-bb__thumb';
        b.setAttribute('aria-label', 'View image ' + (k + 1));
        var im = document.createElement('img'); im.src = src; im.alt = ''; im.loading = 'lazy';
        b.appendChild(im);
        b.addEventListener('click', function () { ii = k; paintMain(); });
        thumbsEl.appendChild(b);
      });
    }
    var money = LeafLP.money;
    // Promo display: advertise a manual sale price with the variant's real price
    // struck through. The actual reduction is applied by an automatic cart discount
    // (so cart/checkout charge the sale price); this only drives the on-page display.
    var promoOn = root.getAttribute('data-promo') === 'true';
    var promoNow = root.getAttribute('data-promo-now') || '';
    var promoNowCents = LeafLP.toCents(promoNow);
    function paintColor() {
      var c = data.colors[ci];
      if (colorName) colorName.textContent = c.name;
      swatches.forEach(function (s, k) {
        s.classList.toggle('is-selected', k === ci);
        s.setAttribute('aria-pressed', k === ci ? 'true' : 'false');
      });
      if (promoOn) {
        if (priceNow) priceNow.textContent = promoNow;
        if (priceWas) { priceWas.textContent = money(c.price); priceWas.hidden = !c.price; }
        if (giftPrice && c.giftValue) giftPrice.textContent = promoNow;
      } else {
        if (priceNow) priceNow.textContent = money(c.price);
        if (priceWas) {
          if (c.compareAt && c.compareAt > c.price) { priceWas.textContent = money(c.compareAt); priceWas.hidden = false; }
          else priceWas.hidden = true;
        }
        if (giftPrice && c.giftValue) giftPrice.textContent = money(c.price);
      }
      if (headerImg) {
        var hsrc = c.headerImage || (c.images && c.images[0]) || '';
        if (hsrc) { headerImg.src = hsrc; headerImg.alt = c.name + ' — Leaf Two'; }
      }
      if (atcBtn) atcBtn.disabled = c.available === false;
      broadcast();
    }
    function broadcast() {
      var c = data.colors[ci];
      var nowCents = promoOn && promoNowCents != null ? promoNowCents : c.price;
      var wasCents = promoOn ? c.price : c.compareAt;
      window.dispatchEvent(new CustomEvent('leaf-lp:buybox', { detail: {
        variantId: c.variantId, colorName: c.name, priceCents: nowCents,
        compareCents: wasCents, available: c.available !== false
      } }));
    }
    function selectColor(k) { ci = k; ii = 0; buildThumbs(); paintMain(); paintColor(); }

    swatches.forEach(function (s, k) { s.addEventListener('click', function () { selectColor(k); }); });

    var prev = root.querySelector('[data-lp-gal-prev]'), next = root.querySelector('[data-lp-gal-next]');
    function step(d) { var g = gallery(); ii = (ii + d + g.length) % g.length; paintMain(); }
    if (prev) prev.addEventListener('click', function () { step(-1); });
    if (next) next.addEventListener('click', function () { step(1); });
    var sx = null, sy = null, stage = root.querySelector('[data-lp-stage]');
    if (stage) {
      stage.addEventListener('touchstart', function (e) { sx = e.touches[0].clientX; sy = e.touches[0].clientY; }, { passive: true });
      stage.addEventListener('touchend', function (e) {
        if (sx == null) return;
        var dx = e.changedTouches[0].clientX - sx, dy = e.changedTouches[0].clientY - sy;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) step(dx < 0 ? 1 : -1);
        sx = sy = null;
      }, { passive: true });
    }

    // Add to cart
    function onAtc() {
      var c = data.colors[ci];
      if (!c.variantId || c.available === false) return;
      atcBtn.classList.add('is-loading');
      var body = { items: [{ id: c.variantId, quantity: 1, properties: data.properties || {} }], sections: ['cart-drawer', 'cart-icon-bubble'] };
      fetch((window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') + '.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(body)
      }).then(function (r) { return r.json(); }).then(function (res) {
        atcBtn.classList.remove('is-loading');
        // Prefer the theme cart drawer if present.
        var drawer = document.querySelector('cart-drawer');
        if (drawer && res.sections && drawer.renderContents) {
          // The host <cart-drawer> carries `is-empty` when the page loaded with an
          // empty cart, and renderContents only swaps #CartDrawer's innards — so the
          // stale class survives and the empty-state CSS hides the header and
          // collapses the new line item. Clear it first, same as the other ATC paths
          // (product-form.js, leaf-product-card.js, custom.js).
          drawer.classList.remove('is-empty');
          var drawerInner = drawer.querySelector('.drawer__inner');
          if (drawerInner) drawerInner.classList.remove('is-empty');
          drawer.renderContents(res);
          if (drawer.open) drawer.open();
        } else { document.dispatchEvent(new CustomEvent('leaf-lp:cart-added', { detail: res })); window.location.href = (window.routes && window.routes.cart_url) || '/cart'; }
      }).catch(function () { atcBtn.classList.remove('is-loading'); window.location.href = '/cart'; });
    }
    if (atcBtn) atcBtn.addEventListener('click', onAtc);

    buildThumbs(); paintMain(); paintColor();

    return function cleanup() {
      swatches.forEach(function (s) { s.replaceWith(s.cloneNode(true)); });
    };
  });

  // ── Sticky mobile ATC ────────────────────────────────────────────────────
  // Appears (mobile only, via CSS) once the hero scrolls out. Two states:
  //  • buy box NOT in view → "CHOOSE…" scrolls to the buy box
  //  • buy box in view (≥ threshold) → "ADD TO CART <price> <compare>" that
  //    proxies a click to the real buy-box ATC (reuses its cart logic).
  // Mirrors price via the `leaf-lp:buybox` event broadcast by the buy box.
  LeafLP.register('sticky', function (root) {
    var btn = root.querySelector('[data-lp-sticky-btn]');
    if (!btn) return;
    var targetSel = root.getAttribute('data-target') || '#leaf-lp-buybox';
    var heroSel = root.getAttribute('data-hero') || '.section-hero-new';
    var showAfter = parseInt(root.getAttribute('data-show-after'), 10) || 600;
    var chooseLabel = btn.getAttribute('data-label-choose') || 'Choose my Leaf Two razor →';
    var atcLabel = btn.getAttribute('data-label-atc') || 'Add to cart';
    var buyEl = document.querySelector(targetSel);
    var heroEl = document.querySelector(heroSel);
    var inBuy = false, heroGone = false, price = null, compare = null;

    var nowEl = btn.querySelector('[data-lp-sticky-now]');
    var wasEl = btn.querySelector('[data-lp-sticky-was]');
    var labelEl = btn.querySelector('[data-lp-sticky-label]');

    function render() {
      root.toggleAttribute('hidden', !heroGone);
      if (inBuy) {
        root.setAttribute('data-mode', 'atc');
        if (labelEl) labelEl.textContent = atcLabel;
        if (nowEl) { nowEl.textContent = price != null ? LeafLP.money(price) : ''; nowEl.hidden = price == null; }
        if (wasEl) { var show = compare != null && compare > price; wasEl.textContent = show ? LeafLP.money(compare) : ''; wasEl.hidden = !show; }
      } else {
        root.setAttribute('data-mode', 'choose');
        if (labelEl) labelEl.textContent = chooseLabel;
        if (nowEl) nowEl.hidden = true;
        if (wasEl) wasEl.hidden = true;
      }
    }

    window.addEventListener('leaf-lp:buybox', function (e) { price = e.detail.priceCents; compare = e.detail.compareCents; render(); });

    var io = null;
    if (buyEl && 'IntersectionObserver' in window) {
      io = new IntersectionObserver(function (entries) { inBuy = entries[0].isIntersecting; render(); }, { threshold: 0.12 });
      io.observe(buyEl);
    }

    var ticking = false;
    function onScroll() {
      if (ticking) return; ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        heroGone = heroEl ? (heroEl.getBoundingClientRect().bottom <= 0) : ((window.pageYOffset || 0) > showAfter);
        render();
      });
    }
    function onClick(e) {
      e.preventDefault();
      if (inBuy) {
        var atc = buyEl && buyEl.querySelector('[data-lp-atc]');
        if (atc) { atc.click(); return; }
      }
      if (buyEl) {
        var y = buyEl.getBoundingClientRect().top + (window.pageYOffset || 0) - 12;
        window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
      }
    }
    btn.addEventListener('click', onClick);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    return function cleanup() {
      btn.removeEventListener('click', onClick);
      window.removeEventListener('scroll', onScroll);
      if (io) io.disconnect();
    };
  });

  // ── Gift tag popup ───────────────────────────────────────────────────────
  LeafLP.register('giftmodal', function (root) {
    var modal = root.querySelector('[data-lp-gift-modal]');
    if (!modal) return;
    var img = modal.querySelector('[data-lp-gm-img]');
    var name = modal.querySelector('[data-lp-gm-name]');
    var price = modal.querySelector('[data-lp-gm-price]');
    var desc = modal.querySelector('[data-lp-gm-desc]');
    var closeBtn = modal.querySelector('[data-lp-gm-close]');
    var tags = Array.prototype.slice.call(root.querySelectorAll('[data-lp-gift]'));
    var lastFocus = null;

    function open(tag) {
      lastFocus = tag;
      if (img)  { img.src = tag.getAttribute('data-gift-img') || ''; img.alt = tag.getAttribute('data-gift-name') || ''; }
      if (name)  name.textContent  = tag.getAttribute('data-gift-name') || '';
      if (price) price.textContent = tag.getAttribute('data-gift-price') || '';
      if (desc)  desc.textContent  = tag.getAttribute('data-gift-desc') || '';
      modal.hidden = false;
      document.body.style.overflow = 'hidden';
      if (closeBtn) closeBtn.focus();
      document.addEventListener('keydown', onKey);
    }
    function close() {
      modal.hidden = true;
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
      if (lastFocus) lastFocus.focus();
    }
    function onKey(e) {
      if (e.key === 'Escape') { close(); return; }
      if (e.key === 'Tab') { // simple focus trap: only ✕ is focusable
        e.preventDefault(); if (closeBtn) closeBtn.focus();
      }
    }
    tags.forEach(function (t) { t.addEventListener('click', function () { open(t); }); });
    if (closeBtn) closeBtn.addEventListener('click', close);
    modal.addEventListener('click', function (e) { if (e.target === modal || e.target.hasAttribute('data-lp-gm-backdrop')) close(); });

    return function cleanup() { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  });

  LeafLP.onReady(function () { LeafLP.init(document); });

  // Theme editor: tear down (clear timers/listeners) then re-init on load/unload.
  document.addEventListener('shopify:section:load', function (e) { LeafLP.init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { teardown(e.target); });
})();
